import { userFacingError } from '../utils/userFacingError';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { socialMediaService } from '../services/socialMediaService';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { loadBackgroundImage, loadCoverImage } from '../utils/imageLoader';
import toast from 'react-hot-toast';

interface BannerGeneratorProps {
  backgroundUrl: string;
  backgroundName: string;
}

const BannerGenerator: React.FC<BannerGeneratorProps> = ({ 
  backgroundUrl, 
  backgroundName 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentIssueNumber, setCurrentIssueNumber] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  const [coverImages, setCoverImages] = useState<HTMLImageElement[]>([]);

  // Aktuelle Ausgabe laden
  const loadCurrentIssue = async () => {
    try {
      const latestIssue = await socialMediaService.getLatestIssue();
      const issueNumber = latestIssue.number;
      if (issueNumber) {
        setCurrentIssueNumber(issueNumber);
        setError(null);
      } else {
        throw new Error('Keine Ausgaben-Nummer in der API-Antwort gefunden');
      }
    } catch (err) {
      const errorMessage = userFacingError(err, 'Unbekannter Fehler');
      setError(`Fehler beim Laden der aktuellen Ausgabe: ${errorMessage}`);
      toast.error(`Aktuelle Ausgabe konnte nicht geladen werden: ${errorMessage}`);
    }
  };

  // Alle 7 Cover laden (aktuell + 6 vorherige)
  const loadAllCovers = useCallback(async (currentNumber: string): Promise<HTMLImageElement[]> => {
    const covers: HTMLImageElement[] = [];
    const currentNum = parseInt(currentNumber);
    
    // Cover von aktuell bis aktuell-6 laden
    for (let i = 0; i < 7; i++) {
      const coverNumber = (currentNum - i).toString();
      try {
        const coverImg = await loadCoverImage(coverNumber, `Cover #${coverNumber} konnte nicht geladen werden`);
        covers.push(coverImg);
      } catch (error) {
        console.warn(`Cover #${coverNumber} konnte nicht geladen werden:`, error);
        // Bei Fehlern trotzdem weitermachen
      }
    }
    
    return covers;
  }, []);

  // Banner generieren
  const generateBanner = useCallback(async () => {
    if (!backgroundUrl || !currentIssueNumber) {
      toast.error('Hintergrund und aktuelle Ausgabe sind erforderlich');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Hintergrund und Cover laden
      const bgImg = await loadBackgroundImage(backgroundUrl);
      const covers = await loadAllCovers(currentIssueNumber);
      
      setBackgroundImage(bgImg);
      setCoverImages(covers);

      // Canvas-Dimensionen setzen (1500x500)
      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error('Canvas nicht gefunden');
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas-Kontext nicht verfügbar');
      }

      // Canvas auf Banner-Größe setzen
      canvas.width = 1500;
      canvas.height = 500;

      // Hintergrund zeichnen
      ctx.drawImage(bgImg, 0, 0, 1500, 500);

      const mitte_h = canvas.width / 2 - 125 + 15 // +15 als Korrektur für optische Mitte
      const mitte_v = canvas.height / 2 - 177
      const rotation_angle = 15

      // Cover-Positionen basierend auf dem Bild
      const coverPositions = [
        { x: mitte_h, y: mitte_v, rotation: rotation_angle },   // Cover 1 
        { x: mitte_h - 600, y: 25, rotation: rotation_angle },   // Cover 2
        { x: mitte_h - 260, y: -100, rotation: rotation_angle },   // Cover 3
        { x: mitte_h - 365, y: 300, rotation: rotation_angle },   // Cover 4 
        { x: mitte_h + 355, y: -100, rotation: rotation_angle },   // Cover 5
        { x: mitte_h + 250, y: 300, rotation: rotation_angle },   // Cover 6
        { x: mitte_h + 600, y: 150, rotation: rotation_angle }   // Cover 7 
      ];

      // Cover zeichnen (von links nach rechts für korrekte Überlappung)
      for (let i = 0; i < covers.length && i < coverPositions.length; i++) {
        const cover = covers[i];
        const pos = coverPositions[i];
        
        // Cover auf 250px Breite skalieren
        const aspectRatio = cover.width / cover.height;
        const scaledWidth = 250;
        const scaledHeight = scaledWidth / aspectRatio;
        
        // Canvas-Kontext für Rotation speichern
        ctx.save();
        
        // Rotationspunkt setzen (Mitte des Cover)
        const centerX = pos.x + scaledWidth / 2;
        const centerY = pos.y + scaledHeight / 2;
        
        // Rotation anwenden
        ctx.translate(centerX, centerY);
        ctx.rotate((pos.rotation * Math.PI) / 180);
        ctx.translate(-centerX, -centerY);
        
        // Schlagschatten für Cover hinzufügen
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 4;
        ctx.shadowOffsetY = 4;
        
        // Cover zeichnen
        ctx.drawImage(cover, pos.x, pos.y, scaledWidth, scaledHeight);
        
        // Schatten zurücksetzen
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        
        // Canvas-Kontext wiederherstellen
        ctx.restore();
      }

      toast.success('Banner erfolgreich generiert!');
    } catch (err) {
      const errorMessage = userFacingError(err, 'Unbekannter Fehler');
      setError(errorMessage);
      toast.error(`Fehler beim Generieren: ${errorMessage}`);
    } finally {
      setIsGenerating(false);
    }
  }, [backgroundUrl, currentIssueNumber, loadAllCovers]);

  // Banner herunterladen
  const downloadBanner = async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error('Kein Banner zum Herunterladen verfügbar');
      return;
    }

    setIsDownloading(true);

    try {
      // Canvas als Blob konvertieren
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Fehler beim Erstellen der Datei');
          return;
        }

        // Download-Link erstellen
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `banner-${backgroundName}-${new Date().toISOString().split('T')[0]}.png`;
        
        // Download auslösen
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // URL freigeben
        URL.revokeObjectURL(url);
        
        toast.success('Banner heruntergeladen!');
      }, 'image/png', 1.0);
    } catch (err) {
      const errorMessage = userFacingError(err, 'Unbekannter Fehler');
      toast.error(`Fehler beim Herunterladen: ${errorMessage}`);
    } finally {
      setIsDownloading(false);
    }
  };

  // Banner automatisch generieren
  useEffect(() => {
    const initializeAndGenerate = async () => {
      await loadCurrentIssue();
      // Warte kurz, damit die Ausgabe geladen ist
      setTimeout(() => {
        if (backgroundUrl && currentIssueNumber) {
          generateBanner();
        }
      }, 500);
    };
    
    initializeAndGenerate();
  }, [backgroundUrl, currentIssueNumber, generateBanner]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-red-800">{error}</span>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* Status-Anzeige für automatische Generierung */}
        {isGenerating && (
          <div className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-200 text-sm font-medium rounded-md text-gray-700 bg-gray-50">
            <LoadingSpinner size="sm" />
            <span className="ml-2">Generiere Banner automatisch...</span>
          </div>
        )}

        {/* Canvas (versteckt) */}
        <canvas
          ref={canvasRef}
          className="hidden"
          width={1500}
          height={500}
        />

        {/* Generierter Banner anzeigen */}
        {backgroundImage && coverImages.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Generierter Banner:</h4>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-center">
                <img
                  src={canvasRef.current?.toDataURL()}
                  alt="Generierter Banner"
                  className="max-w-full h-auto rounded-lg shadow-sm"
                  style={{ maxHeight: '300px' }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Download-Button */}
        {backgroundImage && coverImages.length > 0 && (
          <button
            onClick={downloadBanner}
            disabled={isDownloading}
            className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDownloading ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Lade herunter...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Banner herunterladen
              </>
            )}
          </button>
        )}

        {/* Vorschau-Info */}
        {backgroundImage && coverImages.length > 0 && (
          <div className="text-xs text-gray-500 text-center">
            Dimensionen: 1500 × 500px | {coverImages.length} Cover geladen
          </div>
        )}
      </div>
    </div>
  );
};

export default BannerGenerator;
